import { useSendToAgenciesFormContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.form.context';
import { FC, useContext, useMemo } from 'react';
import { RenderingStateType } from 'types/common.types';
import { createGQLDataContext } from 'utils/createDataContext';
import { useFetchSelectedAgenciesRateDataContext } from './FetchSelectedAgenciesRate.data.context';
import { useFetchSelectedMarketplaceCommunitiesRateDataContext } from './FetchSelectedMarketplaceCommunitiesRate.data.context';

interface IRateData {
  rate: {
    min: number;
    max: number;
  };
  cost: {
    min: number;
    max: number;
  };
}

export const FetchCombinedSummaryRatesDataContext = createGQLDataContext<IRateData>(
  {} as IRateData,
);

export const FetchCombinedSummaryRatesDataProvider: FC = ({ children }) => {
  const agencyRateContext = useFetchSelectedAgenciesRateDataContext();
  const marketplaceRateContext = useFetchSelectedMarketplaceCommunitiesRateDataContext();
  const {
    values: { selectedAgencies, selectedMarketplaceCommunities },
  } = useSendToAgenciesFormContext();

  const combinedData = useMemo(() => {
    const agencyData = agencyRateContext.data;
    const marketplaceData = marketplaceRateContext.data;

    const rates: number[] = [];
    const costs: number[] = [];

    if (agencyData?.rate?.min) rates.push(agencyData.rate.min);
    if (agencyData?.rate?.max) rates.push(agencyData.rate.max);
    if (marketplaceData?.rate?.min) rates.push(marketplaceData.rate.min);
    if (marketplaceData?.rate?.max) rates.push(marketplaceData.rate.max);

    if (agencyData?.cost?.min) costs.push(agencyData.cost.min);
    if (agencyData?.cost?.max) costs.push(agencyData.cost.max);
    if (marketplaceData?.cost?.min) costs.push(marketplaceData.cost.min);
    if (marketplaceData?.cost?.max) costs.push(marketplaceData.cost.max);

    return {
      rate: {
        min: rates.length ? Math.min(...rates) : 0,
        max: rates.length ? Math.max(...rates) : 0,
      },
      cost: {
        min: costs.length ? Math.min(...costs) : 0,
        max: costs.length ? Math.max(...costs) : 0,
      },
    };
  }, [agencyRateContext.data, marketplaceRateContext.data]);

  const status = useMemo((): RenderingStateType => {
    const isAgencyActive = selectedAgencies.length > 0;
    const isMarketplaceActive = selectedMarketplaceCommunities.length > 0;

    if (isAgencyActive && agencyRateContext.status === 'ERROR') return 'ERROR';
    if (isMarketplaceActive && marketplaceRateContext.status === 'ERROR') return 'ERROR';

    if (isAgencyActive && agencyRateContext.status === 'LOADING') return 'LOADING';
    if (isMarketplaceActive && marketplaceRateContext.status === 'LOADING') return 'LOADING';

    return 'READY';
  }, [
    agencyRateContext.status,
    marketplaceRateContext.status,
    selectedAgencies,
    selectedMarketplaceCommunities,
  ]);

  const contextValue = useMemo(
    () => ({
      ...agencyRateContext,
      data: combinedData,
      status,
      __typename: 'GraphQLAPI',
    }),
    [agencyRateContext, status, combinedData],
  );

  return (
    <FetchCombinedSummaryRatesDataContext.Provider value={contextValue}>
      {children}
    </FetchCombinedSummaryRatesDataContext.Provider>
  );
};

export const useFetchCombinedSummaryRatesDataContext = () =>
  useContext(FetchCombinedSummaryRatesDataContext);
